import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import { apiGet, apiPut, apiDel } from 'app/shared-components/util/restAPI';

const configurationsAdapter = createEntityAdapter({
  selectId: (config) => config.id
});

export const fetchConfigurationTypes = createAsyncThunk(
  'configurations/fetchTypes',
  async () => {
    const apiName = 'CoreAPI2';
    const path = '/x2/configuration/types';
    const options = { headers: {}, response: false };
    return await apiGet(apiName, path, options);
  }
);

export const fetchSystemConfigs = createAsyncThunk(
  'configurations/fetchSystemConfigs',
  async (configurationType) => {
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/bysystem?configurationType=${configurationType}`;
    const options = { headers: {}, response: false };
    const data = await apiGet(apiName, path, options);
    return { configurationType, data };
  }
);

export const fetchUserConfigs = createAsyncThunk(
  'configurations/fetchUserConfigs',
  async ({ ownerKey, configurationType }) => {
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/byowner/${ownerKey}?configurationType=${configurationType}`;
    const options = { headers: {}, response: false };
    const data = await apiGet(apiName, path, options);
    return { configurationType, data };
  }
);

export const fetchCombinedConfig = createAsyncThunk(
  'configurations/fetchCombinedConfig',
  async ({ configurationType, identifier, channelId }) => {
    const params = new URLSearchParams({ configurationType, identifier });
    if (channelId) params.append('channelId', channelId);
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/combined?${params.toString()}`;
    const options = { headers: {}, response: false };
    const data = await apiGet(apiName, path, options);
    return { configurationType, identifier, data };
  }
);

export const fetchConfigSchema = createAsyncThunk(
  'configurations/fetchSchema',
  async (configurationType) => {
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/schema?configurationType=${configurationType}`;
    const options = { headers: {}, response: false };
    const schema = await apiGet(apiName, path, options);
    return { configurationType, schema };
  }
);

export const createConfiguration = createAsyncThunk(
  'configurations/create',
  async (configData) => {
    const apiName = 'CoreAPI2';
    const path = '/x2/configuration';
    const options = { headers: {}, body: configData, response: false };
    return await apiPut(apiName, path, options);
  }
);

export const updateConfiguration = createAsyncThunk(
  'configurations/update',
  async ({ id, ...configData }) => {
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/${id}`;
    const options = { headers: {}, body: { id, ...configData }, response: false };
    return await apiPut(apiName, path, options);
  }
);

export const deleteConfiguration = createAsyncThunk(
  'configurations/delete',
  async (id) => {
    const apiName = 'CoreAPI2';
    const path = `/x2/configuration/${id}`;
    const options = { headers: {}, response: false };
    await apiDel(apiName, path, options);
    return id;
  }
);

const configurationsSlice = createSlice({
  name: 'configurations',
  initialState: configurationsAdapter.getInitialState({
    types: [],
    typesLoading: false,
    systemConfigs: {},
    userConfigs: {},
    combinedConfigs: {},
    schemas: {},
    loading: false,
    error: null
  }),
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfigurationTypes.pending, (state) => {
        state.typesLoading = true;
      })
      .addCase(fetchConfigurationTypes.fulfilled, (state, action) => {
        state.types = action.payload;
        state.typesLoading = false;
      })
      .addCase(fetchConfigurationTypes.rejected, (state, action) => {
        state.typesLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchSystemConfigs.fulfilled, (state, action) => {
        state.systemConfigs[action.payload.configurationType] = action.payload.data;
      })
      .addCase(fetchUserConfigs.fulfilled, (state, action) => {
        state.userConfigs[action.payload.configurationType] = action.payload.data;
      })
      .addCase(fetchCombinedConfig.fulfilled, (state, action) => {
        const key = `${action.payload.configurationType}:${action.payload.identifier}`;
        state.combinedConfigs[key] = action.payload.data;
      })
      .addCase(fetchConfigSchema.fulfilled, (state, action) => {
        state.schemas[action.payload.configurationType] = action.payload.schema;
      })
      .addCase(createConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConfiguration.fulfilled, (state, action) => {
        configurationsAdapter.addOne(state, action.payload);
        state.loading = false;
      })
      .addCase(createConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConfiguration.fulfilled, (state, action) => {
        configurationsAdapter.upsertOne(state, action.payload);
        state.loading = false;
      })
      .addCase(updateConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConfiguration.fulfilled, (state, action) => {
        configurationsAdapter.removeOne(state, action.payload);
        state.loading = false;
      })
      .addCase(deleteConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearError } = configurationsSlice.actions;

export const {
  selectAll: selectAllConfigurations,
  selectById: selectConfigurationById
} = configurationsAdapter.getSelectors((state) => state.configurations);

export const selectConfigurationTypes = (state) => state.configurations.types;
export const selectTypesLoading = (state) => state.configurations.typesLoading;
export const selectSystemConfigs = (configurationType) => (state) => 
  state.configurations.systemConfigs[configurationType] || [];
export const selectUserConfigs = (configurationType) => (state) => 
  state.configurations.userConfigs[configurationType] || [];
export const selectCombinedConfig = (configurationType, identifier) => (state) => 
  state.configurations.combinedConfigs[`${configurationType}:${identifier}`];
export const selectConfigSchema = (configurationType) => (state) => 
  state.configurations.schemas[configurationType];
export const selectConfigurationsLoading = (state) => state.configurations.loading;
export const selectConfigurationsError = (state) => state.configurations.error;
export const selectPracticeConfig = (ownerKey) => (state) => {
  const configs = state.configurations.userConfigs['PracticeConfiguration'] || [];
  // Find config by matching ownerId (from ownerKey format: User#userId)
  const ownerId = ownerKey?.split('#')[1] || ownerKey;
  const config = configs.find(c => c.ownerId === ownerId || c.ownerKey === ownerKey);
  return config?.configData;
};

export default configurationsSlice.reducer;
